import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import createId from "@/lib/create-id";
import type { ProviderPresenceInfo, TeamConversationCreate, TeamConversationSummary, TeamMessageSummary, TeamUnreadCounts } from "@/types/team-chat";
import { addMember, addReaction, createConversation, editMessage, getConversation, getConversations, getMessages, getOnlineProviders, getUnreadCounts, hideConversation, leaveConversation, markAsRead, pinConversation, removeReaction, sendMessage, unhideConversation, unpinConversation, updateConversation, updatePresence } from "@/api/team-chat";
import { markTeamConversationNotificationsReadInCache } from "@/query/notifications/notifications";

// Query Keys
export const teamChatQueryKeys = {
  all: ["team-chat"] as const,
  conversations: () => [...teamChatQueryKeys.all, "conversations"] as const,
  conversationDetail: (id: string) => [...teamChatQueryKeys.all, "conversation", id] as const,
  messages: (conversationId: string) => [...teamChatQueryKeys.all, "messages", conversationId] as const,
  unreadCounts: () => [...teamChatQueryKeys.all, "unread-counts"] as const,
  onlineProviders: () => [...teamChatQueryKeys.all, "online-providers"] as const
};

// Hooks
export function useTeamConversations(hidden) {
  const $ = _c(15);
  let t0;
  let t1;
  if ($[0] !== hidden) {
    t0 = [...teamChatQueryKeys.conversations(), {
      hidden
    }];
    t1 = t2 => {
      const {
        pageParam
      } = t2;
      return getConversations(pageParam, hidden);
    };
    $[0] = hidden;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0 || $[4] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      getNextPageParam: _temp,
      initialPageParam: undefined as string | undefined
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery(t2);
  data?.pages;
  let t3;
  if ($[6] !== data?.pages) {
    t3 = data?.pages?.flatMap(_temp2) ?? [];
    $[6] = data?.pages;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const conversations = t3;
  let t4;
  if ($[8] !== conversations || $[9] !== fetchNextPage || $[10] !== hasNextPage || $[11] !== isError || $[12] !== isFetchingNextPage || $[13] !== isLoading) {
    t4 = {
      conversations,
      isLoading,
      isError,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage
    };
    $[8] = conversations;
    $[9] = fetchNextPage;
    $[10] = hasNextPage;
    $[11] = isError;
    $[12] = isFetchingNextPage;
    $[13] = isLoading;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}

/** Prefix for client-generated optimistic conversation IDs that don't exist on
 * the backend yet. Anything created with this prefix should never reach the
 * network — see `isOptimisticConversationId`. */
function _temp2(page) {
  return page?.data ?? [];
}
function _temp(lastPage) {
  return lastPage?.nextCursor ?? undefined;
}
export const OPTIMISTIC_ID_PREFIX = "temp_";

/** True for client-generated optimistic IDs that don't exist on the backend yet. */
export function isOptimisticConversationId(id: string | null | undefined) {
  return !!id && id.startsWith(OPTIMISTIC_ID_PREFIX);
}

/** Predicate for `setQueriesData` that picks the visible (non-hidden)
 * conversations-list queries. Use with `teamChatQueryKeys.conversations()`. */
export function isVisibleConversationsQuery(query: {
  queryKey: readonly unknown[];
}) {
  const opts = query.queryKey[2] as {
    hidden?: boolean;
  } | undefined;
  return !opts?.hidden;
}
export function useTeamConversation(id) {
  const $ = _c(9);
  let t0;
  if ($[0] !== id) {
    t0 = teamChatQueryKeys.conversationDetail(id ?? "");
    $[0] = id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  if ($[2] !== id) {
    t1 = () => getConversation(id);
    t2 = !!id && !isOptimisticConversationId(id);
    $[2] = id;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}
export function useTeamMessages(conversationId) {
  const $ = _c(18);
  let t0;
  if ($[0] !== conversationId) {
    t0 = teamChatQueryKeys.messages(conversationId ?? "");
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== conversationId) {
    t1 = t2 => {
      const {
        pageParam
      } = t2;
      return getMessages(conversationId, pageParam);
    };
    $[2] = conversationId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== conversationId) {
    t2 = !!conversationId && !isOptimisticConversationId(conversationId);
    $[4] = conversationId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      getNextPageParam: _temp3,
      initialPageParam: undefined as string | undefined,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery(t3);
  data?.pages;
  let t4;
  if ($[10] !== data?.pages) {
    t4 = data?.pages?.flatMap(_temp4) ?? [];
    $[10] = data?.pages;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const messages = t4;
  let t5;
  if ($[12] !== fetchNextPage || $[13] !== hasNextPage || $[14] !== isFetchingNextPage || $[15] !== isLoading || $[16] !== messages) {
    t5 = {
      messages,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage
    };
    $[12] = fetchNextPage;
    $[13] = hasNextPage;
    $[14] = isFetchingNextPage;
    $[15] = isLoading;
    $[16] = messages;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp4(page) {
  return page?.data ?? [];
}
function _temp3(lastPage) {
  return lastPage?.nextCursor ?? undefined;
}
export function useTeamUnreadCounts(t0) {
  const $ = _c(3);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = teamChatQueryKeys.unreadCounts();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      queryFn: getUnreadCounts,
      staleTime: Infinity,
      enabled
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
}
export function useSendTeamMessage(conversationId) {
  const $ = _c(16);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== conversationId) {
    t0 = t1 => {
      const {
        content,
        files
      } = t1;
      return sendMessage(conversationId, content, files);
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== conversationId || $[3] !== currentUser?.firstName || $[4] !== currentUser?.id || $[5] !== currentUser?.lastName || $[6] !== currentUser?.profileImage) {
    t1 = async t2 => {
      const {
        content: content_0,
        files: files_0,
        tempId: inputTempId,
        onOptimistic
      } = t2;
      const tempId = inputTempId ?? `pending_${createId("tmp")}`;
      const optimistic = {
        id: tempId,
        conversationId,
        sender: {
          id: currentUser?.id ?? "",
          providerId: currentUser?.id ?? "",
          firstName: currentUser?.firstName ?? "",
          lastName: currentUser?.lastName ?? "",
          profileImage: currentUser?.profileImage ?? null,
          role: "MEMBER"
        },
        content: content_0 || null,
        files: files_0?.map(_temp5) ?? [],
        isDeleted: false,
        editedAt: null,
        reactions: [],
        createdDate: new Date().toISOString(),
        _pending: true
      };
      prependTeamMessage(conversationId, optimistic);
      updateConversationInList(conversationId, conv => ({
        ...conv,
        lastMessageAt: optimistic.createdDate,
        lastMessagePreview: optimistic.content || `📎 ${optimistic.files?.length ?? 1} file`
      }));
      onOptimistic?.();
      return {
        tempId
      };
    };
    $[2] = conversationId;
    $[3] = currentUser?.firstName;
    $[4] = currentUser?.id;
    $[5] = currentUser?.lastName;
    $[6] = currentUser?.profileImage;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  let t3;
  if ($[8] !== conversationId) {
    t2 = (newMessage, _variables, context) => {
      if (!newMessage) {
        return;
      }
      if (context?.tempId) {
        const didReplace = replaceOptimisticWithReal(conversationId, context.tempId, newMessage);
        if (!didReplace) {
          replaceOptimisticOrPrependTeamMessage(conversationId, newMessage);
        }
      } else {
        prependTeamMessage(conversationId, newMessage);
      }
      updateConversationInList(conversationId, conv_0 => ({
        ...conv_0,
        lastMessageAt: newMessage.createdDate,
        lastMessagePreview: newMessage.content || `📎 ${newMessage.files?.length ?? 1} file`
      }));
      queryClient.invalidateQueries({
        queryKey: teamChatQueryKeys.conversations()
      });
    };
    t3 = (_err, _variables_0, context_0) => {
      if (context_0?.tempId) {
        removeMessageFromCache(conversationId, context_0.tempId);
      }
    };
    $[8] = conversationId;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t0 || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: t2,
      onError: t3
    };
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return useMutation(t4);
}
function _temp5(f) {
  return {
    url: "#",
    name: f.name
  };
}
export function useCreateConversation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp6,
      onSuccess: _temp7
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp7(created) {
  if (!created?.id) {
    return;
  }
  queryClient.setQueryData(teamChatQueryKeys.conversationDetail(created.id), created);
  queryClient.setQueryData(teamChatQueryKeys.messages(created.id), {
    pages: [{
      data: [],
      nextCursor: null
    }],
    pageParams: [undefined]
  });
}
function _temp6(data) {
  return createConversation(data);
}
export function useMarkAsRead(conversationId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = {
      mutationFn: () => {
        if (isOptimisticConversationId(conversationId)) {
          return Promise.resolve(null);
        }
        return markAsRead(conversationId);
      },
      onSuccess: result => {
        queryClient.setQueryData(teamChatQueryKeys.unreadCounts(), old => {
          if (!old) {
            return old;
          }
          const updated = {
            ...old
          };
          delete updated[conversationId];
          return updated;
        });
        if (result) {
          markTeamConversationNotificationsReadInCache(conversationId, result.notificationUnreadCount);
        }
      }
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
export function useEditTeamMessage(conversationId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = {
      mutationFn: t1 => {
        const {
          messageId,
          content
        } = t1;
        return editMessage(conversationId, messageId, content);
      },
      onSuccess: updated => {
        if (!updated) {
          return;
        }
        updateTeamMessageInCache(conversationId, updated);
      }
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
export function useOnlineProviders() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: teamChatQueryKeys.onlineProviders(),
      queryFn: getOnlineProviders,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
export function useOnlineStatusMaps() {
  const $ = _c(8);
  const {
    data: onlineData
  } = useOnlineProviders();
  let map;
  if ($[0] !== onlineData?.providers) {
    map = new Map();
    for (const p of onlineData?.providers ?? []) {
      map.set(p.providerId, p.status);
    }
    $[0] = onlineData?.providers;
    $[1] = map;
  } else {
    map = $[1];
  }
  const onlineStatusMap = map;
  let map_0;
  if ($[2] !== onlineData?.providers) {
    map_0 = new Map();
    for (const p_0 of onlineData?.providers ?? []) {
      map_0.set(p_0.providerId, p_0);
    }
    $[2] = onlineData?.providers;
    $[3] = map_0;
  } else {
    map_0 = $[3];
  }
  const presenceMap = map_0;
  let t0;
  if ($[4] !== onlineData || $[5] !== onlineStatusMap || $[6] !== presenceMap) {
    t0 = {
      onlineStatusMap,
      presenceMap,
      onlineData
    };
    $[4] = onlineData;
    $[5] = onlineStatusMap;
    $[6] = presenceMap;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
}
export function usePinConversation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp8,
      onMutate: _temp10,
      onError: _temp11
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp11(_err, _vars, context) {
  for (const [key, data] of context?.previousQueries ?? []) {
    queryClient.setQueryData(key, data);
  }
}
async function _temp10(conversationId_0) {
  await queryClient.cancelQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
  const previousQueries = queryClient.getQueriesData({
    queryKey: teamChatQueryKeys.conversations()
  });
  updateConversationInList(conversationId_0, _temp9);
  return {
    previousQueries
  };
}
function _temp9(conv) {
  return {
    ...conv,
    pinned: true
  };
}
function _temp8(conversationId) {
  return pinConversation(conversationId);
}
export function useUnpinConversation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp12,
      onMutate: _temp14,
      onError: _temp15
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp15(_err, _vars, context) {
  for (const [key, data] of context?.previousQueries ?? []) {
    queryClient.setQueryData(key, data);
  }
}
async function _temp14(conversationId_0) {
  await queryClient.cancelQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
  const previousQueries = queryClient.getQueriesData({
    queryKey: teamChatQueryKeys.conversations()
  });
  updateConversationInList(conversationId_0, _temp13);
  return {
    previousQueries
  };
}
function _temp13(conv) {
  return {
    ...conv,
    pinned: false
  };
}
function _temp12(conversationId) {
  return unpinConversation(conversationId);
}
export function useHideConversation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp16,
      onMutate: _temp17,
      onError: _temp18,
      onSettled: _temp19
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp19() {
  queryClient.invalidateQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
}
function _temp18(_err, _vars, context) {
  for (const [key, data] of context?.previousQueries ?? []) {
    queryClient.setQueryData(key, data);
  }
}
async function _temp17(conversationId_0) {
  await queryClient.cancelQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
  const previousQueries = queryClient.getQueriesData({
    queryKey: teamChatQueryKeys.conversations()
  });
  queryClient.setQueriesData({
    queryKey: teamChatQueryKeys.conversations(),
    predicate: isVisibleConversationsQuery
  }, old => {
    if (!old?.pages?.length) {
      return old;
    }
    return {
      ...old,
      pages: old.pages.map(page => ({
        ...page,
        data: (page.data ?? []).filter(conv => conv.id !== conversationId_0)
      }))
    };
  });
  return {
    previousQueries
  };
}
function _temp16(conversationId) {
  return hideConversation(conversationId);
}
export function useUnhideConversation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp20,
      onMutate: _temp24,
      onError: _temp25,
      onSettled: _temp26
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp26() {
  queryClient.invalidateQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
}
function _temp25(_err, _vars, context) {
  for (const [key, data_0] of context?.previousQueries ?? []) {
    queryClient.setQueryData(key, data_0);
  }
}
async function _temp24(conversationId_0) {
  await queryClient.cancelQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
  const previousQueries = queryClient.getQueriesData({
    queryKey: teamChatQueryKeys.conversations()
  });
  const hiddenQueries = queryClient.getQueriesData({
    queryKey: teamChatQueryKeys.conversations(),
    predicate: _temp21
  });
  let conversationToRestore = null;
  for (const [, data] of hiddenQueries) {
    const pages = (data as {
      pages?: {
        data?: TeamConversationSummary[];
      }[];
    })?.pages;
    for (const page of pages ?? []) {
      const conv = (page.data ?? []).find(c => c.id === conversationId_0);
      if (conv) {
        conversationToRestore = conv;
        break;
      }
    }
    if (conversationToRestore) {
      break;
    }
  }
  if (conversationToRestore) {
    queryClient.setQueriesData({
      queryKey: teamChatQueryKeys.conversations(),
      predicate: _temp22
    }, old => {
      if (!old?.pages?.length) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map(page_0 => ({
          ...page_0,
          data: (page_0.data ?? []).filter(conv_0 => conv_0.id !== conversationId_0)
        }))
      };
    });
    queryClient.setQueriesData({
      queryKey: teamChatQueryKeys.conversations(),
      predicate: _temp23
    }, old_0 => {
      if (!old_0?.pages?.length) {
        return old_0;
      }
      const [first, ...rest] = old_0.pages;
      return {
        ...old_0,
        pages: [{
          ...first,
          data: [conversationToRestore, ...(first.data ?? [])]
        }, ...rest]
      };
    });
  }
  return {
    previousQueries
  };
}
function _temp23(query_1) {
  const opts_1 = query_1.queryKey[2] as {
    hidden?: boolean;
  } | undefined;
  return !opts_1?.hidden;
}
function _temp22(query_0) {
  const opts_0 = query_0.queryKey[2] as {
    hidden?: boolean;
  } | undefined;
  return !!opts_0?.hidden;
}
function _temp21(query) {
  const opts = query.queryKey[2] as {
    hidden?: boolean;
  } | undefined;
  return !!opts?.hidden;
}
function _temp20(conversationId) {
  return unhideConversation(conversationId);
}
export function useUpdateConversation(conversationId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = {
      mutationFn: data => updateConversation(conversationId, data),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: teamChatQueryKeys.conversations()
        });
        queryClient.invalidateQueries({
          queryKey: teamChatQueryKeys.conversationDetail(conversationId)
        });
      }
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
export function useAddMember(conversationId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = {
      mutationFn: providerId => addMember(conversationId, providerId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: teamChatQueryKeys.conversations()
        });
        queryClient.invalidateQueries({
          queryKey: teamChatQueryKeys.conversationDetail(conversationId)
        });
      }
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
export function useLeaveConversation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp27,
      onSuccess: _temp28
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp28() {
  queryClient.invalidateQueries({
    queryKey: teamChatQueryKeys.conversations()
  });
}
function _temp27(conversationId) {
  return leaveConversation(conversationId);
}
export function useUpdatePresence() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp29,
      onSuccess: _temp30
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp30() {
  queryClient.invalidateQueries({
    queryKey: teamChatQueryKeys.onlineProviders()
  });
}
function _temp29(data) {
  return updatePresence(data);
}
export function useAddReaction(conversationId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = {
      mutationFn: t1 => {
        const {
          messageId,
          emoji
        } = t1;
        return addReaction(conversationId, messageId, emoji);
      },
      onSuccess: (result, t2) => {
        const {
          messageId: messageId_0
        } = t2;
        if (!result?.reactions) {
          return;
        }
        updateMessageReactionsInCache(conversationId, messageId_0, result.reactions);
      }
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
export function useRemoveReaction(conversationId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = {
      mutationFn: t1 => {
        const {
          messageId,
          emoji
        } = t1;
        return removeReaction(conversationId, messageId, emoji);
      },
      onSuccess: (result, t2) => {
        const {
          messageId: messageId_0
        } = t2;
        if (!result?.reactions) {
          return;
        }
        updateMessageReactionsInCache(conversationId, messageId_0, result.reactions);
      }
    };
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

// Cache update helpers for real-time
export function updateTeamMessageInCache(conversationId: string, updated: TeamMessageSummary) {
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    return {
      ...old,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pages: old.pages.map((page: any) => ({
        ...page,
        data: (page.data ?? []).map((msg: TeamMessageSummary) => msg.id === updated.id ? updated : msg)
      }))
    };
  });
}
export function prependTeamMessage(conversationId: string, message: TeamMessageSummary) {
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    // Deduplicate by id
    const existingIds = new Set(old.pages.flatMap((p: any) => (p.data ?? []).map((m: TeamMessageSummary) => m.id)));
    if (existingIds.has(message.id)) return old;
    const newPages = [...old.pages];
    newPages[0] = {
      ...newPages[0],
      data: [message, ...(newPages[0].data ?? [])]
    };
    return {
      ...old,
      pages: newPages
    };
  });
}

/**
 * When receiving a message via WebSocket, replace any matching optimistic message
 * (from our own send) to avoid duplicates. If no optimistic match, prepend.
 */
export function replaceOptimisticOrPrependTeamMessage(conversationId: string, realMessage: TeamMessageSummary) {
  const existingIds = new Set<string>();
  let didReplace = false;
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    for (const id of old.pages.flatMap((p: any) => (p.data ?? []).map((m: TeamMessageSummary) => m.id))) {
      existingIds.add(id);
    }
    if (existingIds.has(realMessage.id)) return old;
    const newPages = old.pages.map((page: {
      data?: TeamMessageSummary[];
    }) => {
      const data = page.data ?? [];
      const optimisticIdx = data.findIndex((msg: TeamMessageSummary & {
        _pending?: boolean;
      }) => (msg.id.startsWith("pending_") || msg._pending === true) && msg.sender.providerId === realMessage.sender.providerId);
      if (optimisticIdx >= 0) {
        didReplace = true;
        const optimistic = data[optimisticIdx];
        const newData = [...data];
        newData[optimisticIdx] = {
          ...realMessage,
          _listKey: (optimistic as TeamMessageSummary & {
            _listKey?: string;
          })._listKey ?? optimistic.id
        } as TeamMessageSummary & {
          _listKey?: string;
        };
        return {
          ...page,
          data: newData
        };
      }
      return page;
    });
    if (didReplace) return {
      ...old,
      pages: newPages
    };
    const newPages2 = [...old.pages];
    newPages2[0] = {
      ...newPages2[0],
      data: [realMessage, ...(newPages2[0].data ?? [])]
    };
    return {
      ...old,
      pages: newPages2
    };
  });
}
function removeMessageFromCache(conversationId: string, messageId: string) {
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    return {
      ...old,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pages: old.pages.map((page: any) => ({
        ...page,
        data: (page.data ?? []).filter((msg: TeamMessageSummary) => msg.id !== messageId)
      }))
    };
  });
}

/** Replace optimistic message in place to avoid flash; preserves stable _listKey for React. Returns true if replaced. */
function replaceOptimisticWithReal(conversationId: string, tempId: string, realMessage: TeamMessageSummary): boolean {
  let didReplace = false;
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    const newPages = old.pages.map((page: {
      data?: TeamMessageSummary[];
    }) => {
      const newData = (page.data ?? []).map((msg: TeamMessageSummary & {
        _listKey?: string;
      }) => {
        if (msg.id === tempId) {
          didReplace = true;
          return {
            ...realMessage,
            _listKey: tempId
          } as TeamMessageSummary & {
            _listKey?: string;
          };
        }
        return msg;
      });
      return {
        ...page,
        data: newData
      };
    });
    return didReplace ? {
      ...old,
      pages: newPages
    } : old;
  });
  return didReplace;
}
export function updateConversationInList(conversationId: string, updater: (conv: TeamConversationSummary) => TeamConversationSummary) {
  // Use setQueriesData (plural) for prefix matching — conversation queries
  // are cached with a { hidden } suffix that exact-match setQueryData misses.
  queryClient.setQueriesData({
    queryKey: teamChatQueryKeys.conversations()
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    return {
      ...old,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pages: old.pages.map((page: any) => ({
        ...page,
        data: (page.data ?? []).map((conv: TeamConversationSummary) => conv.id === conversationId ? updater(conv) : conv)
      }))
    };
  });
}
export function incrementUnreadCount(conversationId: string) {
  queryClient.setQueryData(teamChatQueryKeys.unreadCounts(), (old: TeamUnreadCounts | undefined) => ({
    ...old,
    [conversationId]: ((old ?? {})[conversationId] ?? 0) + 1
  }));
}
export function updateReadByInCache(conversationId: string, providerId: string, lastReadMessageDate: string) {
  const readDate = new Date(lastReadMessageDate).getTime();
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    return {
      ...old,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pages: old.pages.map((page: any) => ({
        ...page,
        data: (page.data ?? []).map((msg: TeamMessageSummary) => {
          if (msg.sender.providerId === providerId) return msg;
          const msgDate = new Date(msg.createdDate).getTime();
          if (msgDate > readDate) return msg;
          const readBy = msg.readBy ?? [];
          if (readBy.includes(providerId)) return msg;
          return {
            ...msg,
            readBy: [...readBy, providerId]
          };
        })
      }))
    };
  });
}
export function updateMessageReactionsInCache(conversationId: string, messageId: string, reactions: TeamMessageSummary["reactions"]) {
  queryClient.setQueryData(teamChatQueryKeys.messages(conversationId),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (old: any) => {
    if (!old?.pages?.length) return old;
    return {
      ...old,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pages: old.pages.map((page: any) => ({
        ...page,
        data: (page.data ?? []).map((msg: TeamMessageSummary) => msg.id === messageId ? {
          ...msg,
          reactions
        } : msg)
      }))
    };
  });
}